import type { GetColorDefinition } from 'atoms/colors/colors';
import container from 'atoms/spacing/containers';
import type { PaddingDefinition } from 'atoms/spacing/spacing';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';

import { getBackgroundColor } from 'utils/functions';

import type { ContentfulLayoutSection } from 'graphqlTypes';
import type { FC } from 'react';

interface SectionProps
  extends Omit<ContentfulLayoutSection, 'children' | 'node_locale' | 'contentful_id' | 'internal'> {
  isLandingHero?: boolean;
  isNoNav?: boolean;
}

const largePaddingX = 32;
const maxColumnSize = Number(container.xl.replace('px', '')) + largePaddingX * 2;
const scrollPosition = 'scroll-margin-top: 150px;';

const Section: FC<SectionProps> = ({
  idLink,
  id,
  backgroundColor,
  responsiveBackgroundImages,
  paddingTopDesktop,
  paddingBottomDesktop,
  paddingTopTablet,
  paddingBottomTablet,
  paddingTopMobile,
  paddingBottomMobile,
  isLandingHero,
  isNoNav,
  children,
}) => {
  const { bynderImageDesktop, bynderImageMobile, imageDesktop, imageMobile } = responsiveBackgroundImages || {};

  return (
    <Flex
      as="section"
      id={idLink || id}
      backgroundColor={
        isLandingHero || backgroundColor === 'None'
          ? 'common-transparent'
          : (getBackgroundColor(backgroundColor || 'White') as GetColorDefinition)
      }
      position="relative"
      contain="content"
      justifyContent="center"
      paddingTop={paddingTopMobile !== undefined ? (parseInt(paddingTopMobile) as PaddingDefinition) : 40}
      paddingBottom={paddingBottomMobile !== undefined ? (parseInt(paddingBottomMobile) as PaddingDefinition) : 40}
      width="100%"
      height={isNoNav ? '100vh' : 'fit-content'}
      sm={{
        paddingTop: paddingTopTablet !== undefined ? (parseInt(paddingTopTablet) as PaddingDefinition) : 96,
        paddingBottom: paddingBottomTablet !== undefined ? (parseInt(paddingBottomTablet) as PaddingDefinition) : 96,
      }}
      lg={{
        paddingTop: paddingTopDesktop !== undefined ? (parseInt(paddingTopDesktop) as PaddingDefinition) : undefined,
        paddingBottom:
          paddingBottomDesktop !== undefined ? (parseInt(paddingBottomDesktop) as PaddingDefinition) : undefined,
      }}
      css={scrollPosition}
    >
      {responsiveBackgroundImages && (
        <Container
          position="absolute"
          width="100%"
          right={0}
          left={0}
          top={0}
          bottom={0}
          zIndex={-10}
          css={`
            img {
              object-fit: cover;
              width: 100%;
              height: 100%;
            }
          `}
        >
          <Image
            image={(bynderImageDesktop || imageDesktop || bynderImageMobile || imageMobile) as ImageData}
            display="none"
            md={{ display: 'block' }}
          />
          <Image
            image={(bynderImageMobile || imageMobile || bynderImageDesktop || imageDesktop) as ImageData}
            md={{ display: 'none' }}
          />
        </Container>
      )}
      <Container maxWidth={`${maxColumnSize}px`} width="100%" paddingX={20}>
        {children}
      </Container>
    </Flex>
  );
};

export default Section;
